import { useState } from 'react';
import { useFormulaAssistant } from '../../assistant/useGraphAssistant';

interface FormulaMagicWandProps {
  nodeId: string;
  currentExpression: string;
  onClose: () => void;
}

export const FormulaMagicWand = ({ nodeId, currentExpression, onClose }: FormulaMagicWandProps) => {
  const { isLoading, error, updateFormula, configStatus, resetError } = useFormulaAssistant();
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState<{ expression: string; summary: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || isLoading) return;

    resetError();
    setResult(null);

    const updated = await updateFormula(nodeId, currentExpression, prompt);
    if (updated) {
      setResult(updated);
      setPrompt('');
      // Auto-close after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  return (
    <div className="formula-magic-wand-overlay" onClick={onClose}>
      <div className="formula-magic-wand-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="formula-magic-wand-header">
          <h3>✨ Formula Assistant</h3>
          <button
            type="button"
            className="formula-magic-wand-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {!configStatus.isConfigured && (
          <div className="formula-magic-wand-warning">
            Assistant not configured. Missing: {configStatus.missing.join(', ')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="formula-magic-wand-form">
          <label className="field">
            <span>Describe how to change the formula:</span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., 'Divide by 1000 to convert to thousands', 'Add 20% buffer to the result', 'Guard against division by zero'"
              disabled={!configStatus.isConfigured || isLoading}
              rows={3}
              autoFocus
            />
          </label>

          {error && (
            <div className="formula-magic-wand-error">
              {error}
            </div>
          )}

          {result && (
            <div className="formula-magic-wand-success">
              ✓ Updated: {result.summary}
            </div>
          )}

          <div className="formula-magic-wand-actions">
            <button
              type="button"
              className="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary"
              disabled={!prompt.trim() || isLoading || !configStatus.isConfigured}
            >
              {isLoading ? 'Updating...' : 'Update Formula'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

